import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { CreateClientMarketOrderMutationRequest, CreateClientMarketOrderMutationResponse, CreateClientMarketOrderPathParams, CreateClientMarketOrder400, CreateClientMarketOrder404 } from "../types/CreateClientMarketOrder";
import type { UseMutationOptions } from "@tanstack/react-query";

 type CreateClientMarketOrderClient = typeof client<CreateClientMarketOrderMutationResponse, CreateClientMarketOrder400 | CreateClientMarketOrder404, CreateClientMarketOrderMutationRequest>;
type CreateClientMarketOrder = {
    data: CreateClientMarketOrderMutationResponse;
    error: CreateClientMarketOrder400 | CreateClientMarketOrder404;
    request: CreateClientMarketOrderMutationRequest;
    pathParams: CreateClientMarketOrderPathParams;
    queryParams: never;
    headerParams: never;
    response: CreateClientMarketOrderMutationResponse;
    client: {
        parameters: Partial<Parameters<CreateClientMarketOrderClient>[0]>;
        return: Awaited<ReturnType<CreateClientMarketOrderClient>>;
    };
};
/**
 * @description Creates a new order for a given client market. The client order reference
should be unique for the client market on the same day. but this uniqueness
is not enforced. Creating the order will not fail here but will be rejected
when the order is sent to the market.
 * @summary Create
 * @link /api/clients/:clientCode/markets/:marketCode/orders/:clientOrderRef
 */
export function useCreateClientMarketOrder(clientCode: CreateClientMarketOrderPathParams["clientCode"], marketCode: CreateClientMarketOrderPathParams["marketCode"], clientOrderRef: CreateClientMarketOrderPathParams["clientOrderRef"], options: {
    mutation?: UseMutationOptions<CreateClientMarketOrder["response"], CreateClientMarketOrder["error"], CreateClientMarketOrder["request"]>;
    client?: CreateClientMarketOrder["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<CreateClientMarketOrder["data"], CreateClientMarketOrder["error"], CreateClientMarketOrder["request"]>({
                method: "post",
                url: `/api/clients/${clientCode}/markets/${marketCode}/orders/${clientOrderRef}`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}